import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { DISCONNECT_HISTORY_VISIBLE_COUNT } from "static/constants";

/**
 * The trail holds one entry per status the manufacturer reported. A reconnect date
 * closes the disconnect that entry belongs to, so the entries fall into episodes on
 * that boundary: everything up to and including a closed entry is one disconnect.
 */
const groupIntoDisconnects = (entries) => {
  const episodes = [];
  let current = [];

  entries.forEach((entry) => {
    current.push(entry);

    if (entry?.reconnected_at) {
      episodes.push(current);
      current = [];
    }
  });

  if (current.length > 0) {
    episodes.push(current);
  }

  return episodes.map((statuses) => ({
    statuses,
    disconnectedAt: statuses[0]?.recorded_at,
    reconnectedAt: statuses[statuses.length - 1]?.reconnected_at || null,
    latestStatus: statuses[statuses.length - 1]?.status || "",
    latestRecordedAt: statuses[statuses.length - 1]?.recorded_at,
  }));
};

/**
 * Newest first, for display. Sorts a copy (the store's array is frozen). The stored
 * order breaks ties and covers entries without a usable date.
 */
export const sortStatusesNewestFirst = (statuses) =>
  [...statuses].sort(
    (a, b) => new Date(b?.recorded_at) - new Date(a?.recorded_at) || 0
  );

const useDisconnectHistory = () => {
  const { deviceInformation } = useSelector((state) => state.transmission);
  const [showAll, setShowAll] = useState(false);

  const episodes = useMemo(() => {
    const entries = deviceInformation?.monitor_disconnected_history;

    return Array.isArray(entries) ? groupIntoDisconnects(entries) : [];
  }, [deviceInformation?.monitor_disconnected_history]);

  // A disconnect with no reconnect date is the one the monitor is still in.
  const active = useMemo(
    () => episodes.find((episode) => !episode.reconnectedAt) || null,
    [episodes]
  );

  const resolved = useMemo(
    () => episodes.filter((episode) => episode.reconnectedAt).reverse(),
    [episodes]
  );

  return {
    active,
    resolved,
    showAll,
    hasMore: resolved.length > DISCONNECT_HISTORY_VISIBLE_COUNT,
    visibleResolved: showAll
      ? resolved
      : resolved.slice(0, DISCONNECT_HISTORY_VISIBLE_COUNT),

    setShowAll,
  };
};

export default useDisconnectHistory;
